import React, { useContext, useEffect, useState } from 'react';
import { Box, ButtonBase, Tooltip } from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import { useSnackbar, VariantType } from 'notistack';
import moment from 'moment';
import FileSaver from 'file-saver';
import { isNullOrUndefined } from 'util';
import * as R from 'ramda';
import _ from 'lodash';
import { ApplicationContext, AppContext } from '../../context/Contexts';
import { useAppSelector } from '../../store/configureStore';
import { useStyles, cx } from '../../custom-forms';
import { CopyButton, CustomIcon, CustomInputText, NumberFormatCustom } from '../../custom-forms';
import AlternativeCalculationPart from '../alternative-calculation/components/AlternativeCalculationPart';
import AlternativeCalculationPiecesHeader from '../alternative-calculation/components/AlternativeCalculationPiecesHeader';
import AlternativeCalculationToolbar from '../alternative-calculation/components/AlternativeCalculationToolbar';
import { CalculationFlagsProvider } from '../alternative-calculation/context/CalculationFlagsContext';
import { buildGridTemplateFrom } from '../alternative-calculation/config/columns';
import { buildAltCalcGridColumns } from '../alternative-calculation/config/altCalcGridColumns';
import {
  CalculationActions,
  ColumnFilter,
  ColumnKey,
  FiltersState,
  SortState
} from '../alternative-calculation/types/alternative-calculation.types';
import {
  filterOptionLabel,
  filterOptionsFor
} from '../alternative-calculation/utils/calculation.utils';
import {
  applyOrderPartCodeChange,
  formatDeliveryDate,
  getPackageFromOrderParts,
  initOrderPartDTO,
  ORDER_PART_WORKFLOW_CALCULATION
} from './order.utils';
import { buildOrderGridColumns, OrderGridColumnsCtx } from './orderGridColumns';
import { CaseCalculationPart } from '../../interfaces/Case';
import RequeryDialog from './RequeryDialog';
import OrderHistoryTimeline from './OrderHistoryTimeline';
import ChangeOrderStatusDialog from './ChangeOrderStatusDialog';
import ExtendDeliveryDialog from './ExtendDeliveryDialog';
import DeliveryDateHistoryDialog from './DeliveryDateHistoryDialog';
import OrderDetailsHeader from './OrderDetailsHeader';
import OrderDetailsTotals from './OrderDetailsTotals';
import Referentials from '../../helpers/Referentials.json';
import {
  AttachmentTypeCode,
  CarPartQualityTypeCode,
  CountryCodeEnum,
  OrderNotificationTemplateEnum,
  OrderPartStatus,
  OrderStatusCode,
  OrderStatusTypesCode,
  OrganizationTypeCode,
  ParametreCode,
  ReferentialCode,
  RoleClaimsEnum,
  SupplierCode
} from '../../helpers/Constants';
import { Utils } from '../../helpers/Utils';
import { pretUnitarFormat } from '../../utils/orderUtils';
import { useCalculationPartsActions } from '../alternative-calculation/hooks/useCalculationPartsActions';
import { IReferential } from '../../interfaces/IReferential';
import { Organization } from '../../interfaces/Organization';
import { OrderParts } from '../../interfaces/OrderParts';
import {
  ExtendDeliveryRequestDto,
  ExtendDeliveryResponseDto,
  Order,
  OrderPart,
  OrderPartDeliveryDateHistory,
  OrderPartDTO,
  OrderStatusChangeRequestDto,
  OrderStatusHistory,
  PartToBeOrdered,
  UpdateOrderAmountsRequestDto
} from '../../interfaces/Order';
import {
  CaseAttachment,
  CaseCalculationSupplierAlternativePart,
  GetSuppliersAlternativePartsRequestDto,
  Model,
  NonStockPart,
  TariffPercentResponse,
  UpdateOrderInfoRequest
} from '../../interfaces/Case';
import {
  GetSuppliersByStatusRequestDto,
  Workflow,
  WorkflowSupplier
} from '../../interfaces/Workflow';

const MAX_EXTENSION_DAYS = 30;

export interface ExternalOrderDetailsNewProps {
  orderDetails: Order;
  onOrderChange: (order: Order) => void;
  hasRights: boolean;
  additionTariff: TariffPercentResponse;
  discountTariff: TariffPercentResponse;
  automatePlaceOrder: boolean;
  workflowAutomatePlaceOrder: boolean;
  automatePlaceOrderChange: () => Promise<void>;
  onOrderPlacedSuccessfully: () => void;
  fullBleed: boolean;
}

const OrderDetailsNew: React.FC<ExternalOrderDetailsNewProps> = (props) => {
  const {
    hasRights,
    additionTariff,
    discountTariff,
    automatePlaceOrder,
    workflowAutomatePlaceOrder,
    automatePlaceOrderChange,
    onOrderPlacedSuccessfully
  } = props;

  const classes = useStyles();
  const context = useContext(ApplicationContext) as AppContext;
  const orderService = context.orderService;
  const caseService = context.caseService;
  const organizationService = context.organizationService;
  const workflowService = context.workflowService;
  const appReferentialService = context.referentialService;
  const appUserService = context.appUserService;
  const translatorService = context.translatorService;
  const refreshService = context.refreshService;
  const { enqueueSnackbar } = useSnackbar();
  const onNotify = (message: string, variant: VariantType) => enqueueSnackbar(message, { variant });

  const caseSettingsState = useAppSelector((s) => s.caseSettings);
  const appState = useAppSelector((s) => s.app);
  const appUser = appState!.appUser;
  const country = appUser?.organization?.country ?? null;

  const [order, setOrderState] = useState<Order>(props.orderDetails);
  const applyOrderChange = (o: Order) => {
    setOrderState(o);
    props.onOrderChange(o);
  };

  const mergeRefreshedOrder = (refreshed: Order, orderParts: OrderPart[]): Order => ({
    ...order,
    orderStatus: refreshed.orderStatus,
    orderStatusId: refreshed.orderStatusId,
    amount: refreshed.amount,
    amountTVA: refreshed.amountTVA,
    amountWithoutTVA: refreshed.amountWithoutTVA,
    orderParts
  });

  const [orderpartsList, setOrderparts] = useState<OrderPartDTO[]>([]);
  const [orderStatusHistory, setOrderStatusHistory] = useState<OrderStatusHistory[]>([]);
  const [attachments, setAttachments] = useState<CaseAttachment[]>([]);
  const [changeOrderStatusComment, setChangeOrderStatusComment] = useState('');
  const [TVA, setTVA] = useState('0');

  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(true);
  const [userStartCode, setUserStartCode] = useState('');
  const [statuses, setStatuses] = useState<IReferential[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<IReferential[]>([]);
  const [orderPartStatus, setOrderPartStatus] = useState<IReferential[]>([]);
  const [attachType, setAttachType] = useState<IReferential | null>(null);
  const [partsLocationOrganization, setPartsLocationOrganization] = useState<Organization | null>(
    null
  );
  const [showOrderPrice, setShowOrderPrice] = useState(false);
  const [costCenter, setCostCenter] = useState<IReferential | null>(null);
  const [executing, setExecuting] = useState(false);
  const [isExtendingDelivery, setIsExtendingDelivery] = useState(false);
  const [quality, setQuality] = useState<IReferential | null>(null);
  const [firstCasePartToBeOrdered, setFirstCasePartToBeOrdered] = useState<PartToBeOrdered | null>(
    null
  );
  const [hasSupplierRole, setHasSupplierRole] = useState(false);
  const [model, setModel] = useState<Model | null>(null);
  const [interventionType, setInterventionType] = useState<IReferential | null>(null);
  const [workflow, setWorkflow] = useState<Workflow | null>(null);

  const [isChangeOrderStatusDialogOpen, setIsChangeOrderStatusDialogOpen] = useState(false);
  const [isRequeryDialogOpen, setIsRequeryDialogOpen] = useState(false);
  const [pendingDeletePartId, setPendingDeletePartId] = useState<number | null>(null);
  const [pendingUpdateStatusPartId, setPendingUpdateStatusPartId] = useState<number | null>(null);
  const [isCancelPartRequeryDialogOpen, setIsCancelPartRequeryDialogOpen] = useState(false);
  const [extensionDays, setExtensionDays] = useState<number | null>(null);
  const [isExtendDialogOpen, setIsExtendDialogOpen] = useState(false);
  const [selectedOrderPart, setSelectedOrderPart] = useState<OrderPartDTO | null>(null);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [historyDialogData, setHistoryDialogData] = useState<OrderPartDeliveryDateHistory[]>([]);
  const [nonStockSuppliers, setNonStockSuppliers] = useState<WorkflowSupplier[]>([]);

  // Sort/filter state for the top order header (order columns; controls disabled, kept for the props).
  const [orderSort] = useState<SortState>({ column: null, dir: 1 });
  const [orderFilters] = useState<FiltersState>({});

  // Per-orderpart sort/filter for the interogation results — each expanded part has a dedicated header.
  const [variantSort, setVariantSort] = useState<{ [id: number]: SortState }>({});
  const [variantFilters, setVariantFilters] = useState<{ [id: number]: FiltersState }>({});
  const variantSortOf = (id: number): SortState => variantSort[id] ?? { column: null, dir: 1 };
  const variantFiltersOf = (id: number): FiltersState => variantFilters[id] ?? {};

  // Shared alt-calc part operations from the alternative-calculation hook; order-specific ops stay below.
  const {
    toggleExpand,
    interogate,
    interogateAlternativeParts,
    handleAlternativePartSelect,
    toggleAlternativePartSelection,
    clearRecommendation,
    unselectPart,
    unselectAll,
    updateGuideNumber,
    updatePieceNr,
    updateOriginalPrice,
    updateCarPartCategory,
    updateAlternativePartDescription,
    validateSelectedParts
  } = useCalculationPartsActions<OrderPartDTO>({
    calculationParts: orderpartsList,
    setCalculationParts: setOrderparts,
    additionTariff,
    discountTariff,
    workflow: workflow!,
    workflowCalculation: ORDER_PART_WORKFLOW_CALCULATION,
    ownStockOrganization: null,
    variantsEnabled: false,
    nonStockSuppliers,
    setNonStockSuppliers,
    getPartsLocationOrganizationId: () =>
      order.partsLocationOrganizationId ?? appUser!.organizationId ?? null,
    getInterrogationOrganizationId: () => order.partsLocationOrganizationId ?? null,
    interrogationSupplier: { supplierId: order.supplierId ?? null, quality },
    reuseGroupedUniqueId: true
  });

  const isEmpty = (value: string) => value == null || /^ *$/.test(value);

  // A row is expanded when the shared hook has opened it (chevron / interrogate).
  const someRowExpanded = () => orderpartsList.some((op) => op.isExpanded);

  const hasRight = (right: string): boolean => {
    return appState!.user!.profile.role.includes(right);
  };

  const isNull = (value: any, nullValue: any) => (isNullOrUndefined(value) ? nullValue : value);

  const getStatusValue = (): IReferential | null => {
    let statusValue: IReferential | null = order.orderStatus;
    if (orderStatusHistory && orderStatusHistory[0]) {
      statusValue = orderStatusHistory[0].orderStatus;
    }
    return statusValue;
  };

  const isOrderDelivered = (): boolean => getStatusValue()?.code === OrderStatusCode.REP_LIVRATA;

  const isDeliveryActionsVisible = (): boolean => {
    if (country?.code === CountryCodeEnum.CZ.toString()) {
      return false;
    }
    const code = getStatusValue()?.code;
    return (
      code === OrderStatusCode.REP_PLASATA ||
      code === OrderStatusCode.FZR_COMANDA_CONFIRMATA ||
      code === OrderStatusCode.FZR_COMANDA_CONFIRMATA_PARTIAL
    );
  };

  const isDisplayInvoiceColumn = (): boolean => {
    const hasLivrataOrPartialLivrataPart = orderpartsList.some(
      (part) =>
        part.status?.code === OrderPartStatus.LIVRATA ||
        part.status?.code === OrderPartStatus.PARTIAL_LIVRATA
    );
    return hasLivrataOrPartialLivrataPart || isOrderDelivered();
  };

  const isAwaitingSupplierConfirmation = (): boolean => {
    if (getStatusValue()?.code !== OrderStatusCode.REP_PLASATA) {
      return false;
    }
    return (
      order.supplier?.name == SupplierCode.AUGSBURG || order.supplier?.name == SupplierCode.AUTONET
    );
  };

  const canUpdateOrderPartStatus = (part: OrderPartDTO): boolean => {
    if (order.clientId !== appUser!.organizationId) {
      return false;
    }
    if (isAwaitingSupplierConfirmation()) {
      return false;
    }
    return (
      part.status?.code === OrderPartStatus.PROCESSED ||
      part.status?.code === OrderPartStatus.PROCESSING ||
      part.status?.code === OrderPartStatus.CONFIRMATA_PARTIAL
    );
  };

  const getNextOrderStatus = (): IReferential | undefined => {
    const currentStatus = getStatusValue();
    if (currentStatus?.code === OrderStatusCode.REP_DRAFT) {
      return statuses.find((item) => item.code === OrderStatusCode.REP_PLASATA);
    }
    if (currentStatus?.code === OrderStatusCode.FZR_VERIFICARE_LA_FURNIZOR) {
      return statuses.find((item) => item.code === OrderStatusCode.REP_VERIFICATA_DE_FURNIZOR);
    }
    if (currentStatus?.code === OrderStatusCode.REP_VERIFICATA_DE_FURNIZOR) {
      return someRowExpanded()
        ? statuses.find((item) => item.code === OrderStatusCode.REP_REINTEROGATED_PARTS)
        : statuses.find((item) => item.code === OrderStatusCode.REP_PLASATA);
    }
    if (currentStatus?.code === OrderStatusCode.REP_REINTEROGATED_PARTS) {
      return statuses.find((item) => item.code === OrderStatusCode.REP_PLASATA);
    }
    return undefined;
  };

  const canChangeNextOrderStatus = () => {
    const currentStatus = getStatusValue();
    return (
      !hasRights ||
      !currentStatus?.code?.includes(userStartCode) ||
      !(getNextOrderStatus() != undefined)
    );
  };

  const getOrderButton = (): string | undefined => {
    const currentStatus = getStatusValue();
    if (
      currentStatus?.code == OrderStatusCode.REP_VERIFICATA_DE_FURNIZOR.toString() &&
      hasSupplierRole
    ) {
      return currentStatus?.displayName;
    }
    if (currentStatus?.code == 'REP_DRAFT') {
      return automatePlaceOrder
        ? translatorService.Tranlate('CASE_CAR_CALCULATIONS_CONFIRM_BUTTON', 'Confirma')
        : translatorService.Tranlate('ORDER_PART_PLACE_ORDER_LABEL', 'Lanseaza Comanda');
    } else {
      if (currentStatus?.code == OrderStatusCode.FZR_VERIFICARE_LA_FURNIZOR.toString()) {
        return translatorService.Tranlate('ORDER_DETAILS_SAVE_BUTTON', 'Salveaza');
      }
      if (currentStatus?.code == OrderStatusCode.REP_VERIFICATA_DE_FURNIZOR.toString()) {
        return someRowExpanded()
          ? translatorService.Tranlate('ORDER_DETAILS_SAVE_BUTTON', 'Salveaza')
          : translatorService.Tranlate('ORDER_PART_PLACE_ORDER_LABEL', 'Lanseaza Comanda');
      }
      if (currentStatus?.code == OrderStatusCode.REP_REINTEROGATED_PARTS.toString()) {
        return translatorService.Tranlate('ORDER_PART_PLACE_ORDER_LABEL', 'Lanseaza Comanda');
      }
      return currentStatus?.displayName;
    }
  };

  const hideSaveButton = () => {
    const currentStatus = getStatusValue();
    if (currentStatus?.code === OrderStatusCode.FZR_VERIFICARE_LA_FURNIZOR && !hasSupplierRole) {
      return true;
    }
    return false;
  };

  const allPartsInterogated = () => {
    const currentStatus = getStatusValue();
    if (currentStatus?.code === OrderStatusCode.REP_VERIFICATA_DE_FURNIZOR && !hasSupplierRole) {
      const nonInterogatedParts = [...orderpartsList].filter(
        (orderPart) => orderPart.hasBeenVerifiedBySupplier && !orderPart.supplierId
      );
      if (nonInterogatedParts.length > 0) {
        return false;
      }
    }
    return true;
  };

  const viewDeletePart = (): boolean => {
    const currentStatus = getStatusValue();
    if (
      currentStatus?.code === OrderStatusCode.REP_DRAFT ||
      currentStatus?.code === OrderStatusCode.REP_REINTEROGATED_PARTS
    ) {
      return true;
    }
    if (currentStatus?.code === OrderStatusCode.REP_VERIFICATA_DE_FURNIZOR && !someRowExpanded()) {
      return true;
    }
    return false;
  };

  const checkIfOrderPartsVerifiedBySupplier = () =>
    orderpartsList.some((orderPart) => orderPart.hasBeenVerifiedBySupplier);

  // Recompute totals from the parts list + TVA and sync them onto the order (idem old updateAmount).
  useEffect(() => {
    if (isLoadingOrderDetails) {
      return;
    }
    const partList = new OrderParts(
      orderpartsList.concat(getPackageFromOrderParts(orderpartsList)),
      parseFloat(TVA)
    );
    const updated = { ...order };
    updated.amountWithoutTVA = partList.totalWithoutVAT();
    updated.amountTVA = partList.totalVAT();
    updated.amount = partList.total();
    applyOrderChange(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderpartsList, TVA, isLoadingOrderDetails]);

  const loadData = async () => {
    const currentOrder = order;
    const refOrderStatus = Referentials.referential.find(
      (item) => item.code === ReferentialCode.OrderStatus
    );
    const refOrderPartStatus = Referentials.referential.find(
      (item) => item.code === ReferentialCode.OrderPartStatus
    );
    const ref = Referentials.referential.find(
      (item) => item.code === ReferentialCode.AttachmentType
    );
    const refQualityPart = Referentials.referential.find(
      (item) => item.code === ReferentialCode.CarPartCategory
    );

    const orderParts = currentOrder.orderParts;
    const workflowId =
      caseSettingsState?.caseSettings?.workflow.id ?? currentOrder.case?.caseWorkflow?.workflowId;
    const [
      orderStatus,
      orderPartStatusRef,
      statusHist,
      attachmentTypes,
      caseEvent,
      partQualityTypes,
      casePartToBeOrdered,
      suppliersInit,
      workflowInit
    ] = await Promise.all([
      appReferentialService.Get(refOrderStatus!.baseUrl),
      appReferentialService.Get(refOrderPartStatus!.baseUrl),
      orderService.GetOrderStatusHistories(currentOrder.id),
      appReferentialService.Get(ref!.baseUrl),
      caseService.GetCurrentEvent(currentOrder.caseId),
      appReferentialService.Get(refQualityPart!.baseUrl),
      orderParts && orderParts.length > 0
        ? orderService.GetPartToBeOrdered(orderParts[0].casePartToBeOrderedId)
        : null,
      !currentOrder.supplier
        ? organizationService.GetUserOrganizationsByIds([currentOrder.supplierId])
        : [],
      workflowId ? workflowService.GetUserWorkflow(workflowId) : null
    ]);

    const nextOrder = { ...currentOrder };
    if (!nextOrder.supplier) {
      const supplier = suppliersInit.find((item) => item.id === nextOrder.supplierId);
      nextOrder.supplier = supplier === undefined ? null : supplier;
    }

    const modelId =
      caseSettingsState?.case?.caseVehicle?.modelId ?? nextOrder?.caseVehicle?.modelId;
    const loadedModel = modelId ? await caseService.GetModelById(modelId) : null;

    const [users] = await Promise.all([
      appUserService.GetUsersInfo(Array.from(new Set(statusHist.map((item) => item.createdBy))))
    ]);

    statusHist.forEach((p) => {
      const user = users.find((item) => item.id === p.createdBy);
      p.createdByUser = user === undefined ? null : user;
    });

    let partsLocationOrg: Organization | null = null;
    if (!isNullOrUndefined(nextOrder.partsLocationOrganizationId)) {
      partsLocationOrg = await organizationService.GetOrganization(
        nextOrder.partsLocationOrganizationId!
      );
    }

    let center: IReferential | null = null;
    if (!R.isNil(caseEvent) && !isNullOrUndefined(caseEvent.organizationId)) {
      center = await workflowService.GetCostCenter(caseEvent.organizationId!);
    }

    const showPrice = !appUser!.organization!.organizationType!.hideOrderPrices;
    let tvaValue = '0';
    if (nextOrder.amountWithoutTVA !== 0) {
      tvaValue = ((nextOrder.amountTVA / nextOrder.amountWithoutTVA) * 100).toFixed(0);
    } else {
      const caseParameterHistory = await caseService.GetCaseParameterHistory(nextOrder.caseId);
      const tvaParam = caseParameterHistory.find(
        (item) => item.parameterCode === ParametreCode.TVA
      );
      if (tvaParam) {
        tvaValue = tvaParam.value;
      }
    }

    const startCode =
      appUser!.organization!.organizationType!.code === OrganizationTypeCode.SUPPLIER
        ? OrderStatusTypesCode.FZR
        : OrderStatusTypesCode.REP;

    const orderPartStatusFiltered = orderPartStatusRef.filter((item) => item.isActive === true);
    const x = nextOrder.orderParts!.map(initOrderPartDTO);
    for (let i = 0; i < x.length; i++) {
      x[i].isLoading = false;
      x[i].alternativeParts = [];
      x[i].hasEmptyGuideNumber = isEmpty(x[i].guideNumber);
      x[i].hasEmptyAlternativeGuideNumber = isEmpty(x[i].alternativeGuideNumber ?? '');
    }

    const supplierRole = hasRight(RoleClaimsEnum.Supplier);

    const expandVerified =
      nextOrder.orderStatus?.code === OrderStatusCode.REP_VERIFICATA_DE_FURNIZOR && !supplierRole;
    x.forEach((row) => {
      row.isExpanded = expandVerified && (row.hasBeenVerifiedBySupplier ?? false);
    });

    setOrderState(nextOrder);
    setUserStartCode(startCode);
    setOrderparts(x);
    setStatuses(orderStatus.filter((item) => item.isActive === true));
    setOrderStatuses(orderStatus.filter((item) => item.isActive === true));
    setOrderPartStatus(orderPartStatusFiltered);
    setOrderStatusHistory(statusHist);
    setTVA(tvaValue);
    setAttachType(attachmentTypes.find((item) => item.code === AttachmentTypeCode.OTHER)!);
    setPartsLocationOrganization(partsLocationOrg);
    setShowOrderPrice(showPrice);
    setCostCenter(center);
    setQuality(
      partQualityTypes.find((item) => item.id === nextOrder.supplier?.categoryPartId) ?? null
    );
    setHasSupplierRole(supplierRole);
    setFirstCasePartToBeOrdered(casePartToBeOrdered);
    setModel(loadedModel);
    setInterventionType(!R.isNil(caseEvent) ? caseEvent.interventionType : null);
    setWorkflow(workflowInit);
    setIsLoadingOrderDetails(false);
  };

  useEffect(() => {
    setIsLoadingOrderDetails(true);
    loadData();
    const sub = refreshService.OrdersToRefresh$().subscribe((orderIds: number[]) => {
      if (orderIds.includes(order.id)) {
        setIsLoadingOrderDetails(true);
        loadData();
      }
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOECodeChange = (event: React.ChangeEvent<HTMLInputElement>, orderPart: OrderPart) =>
    setOrderparts(applyOrderPartCodeChange(orderpartsList, orderPart.id, event.target.value, true));

  const handleAMCodeChange = (event: React.ChangeEvent<HTMLInputElement>, orderPart: OrderPart) =>
    setOrderparts(
      applyOrderPartCodeChange(orderpartsList, orderPart.id, event.target.value, false)
    );

  const handleClientOECodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    orderPart: OrderPart
  ) => {
    const orderParts = [...orderpartsList];
    const index = orderParts.findIndex((item) => item.id === orderPart.id);
    if (index !== -1) {
      orderParts[index] = { ...orderParts[index], guideNumber: event.target.value };
      setOrderparts(orderParts);
    }
  };

  const handleClientAMCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    orderPart: OrderPart
  ) => {
    const orderParts = [...orderpartsList];
    const index = orderParts.findIndex((item) => item.id === orderPart.id);
    if (index !== -1) {
      orderParts[index] = { ...orderParts[index], alternativeGuideNumber: event.target.value };
      setOrderparts(orderParts);
    }
  };

  const getInactiveWorkflowSuppliers = async () => {
    if (!workflow?.inactiveSupplierNotification) {
      return;
    }
    const inactiveSuppliers = await workflowService.GetWorkflowSuppliersByStatus({
      workflowId: workflow.id,
      organizationId: order.partsLocationOrganizationId ?? appUser!.organizationId!,
      isActive: false,
      supplierId: order.supplierId
    } as GetSuppliersByStatusRequestDto);

    if (inactiveSuppliers.length > 0) {
      enqueueSnackbar(
        (inactiveSuppliers.length == 1
          ? translatorService.Tranlate(
              'INACTIVE_SUPPLIERS_MSG_FIRST_PART_SINGLE',
              'Achizitia de la furnizorul'
            )
          : translatorService.Tranlate(
              'INACTIVE_SUPPLIERS_MSG_FIRST_PART_MULTIPLE',
              'Achizitia de la furnizorii'
            )) +
          ' ' +
          inactiveSuppliers.map((supplier) => supplier.supplierName).join(', ') +
          ' ' +
          (inactiveSuppliers.length == 1
            ? translatorService.Tranlate(
                'INACTIVE_SUPPLIERS_MSG_SECOND_PART_SINGLE',
                'este momentan blocată!'
              )
            : translatorService.Tranlate(
                'INACTIVE_SUPPLIERS_MSG_SECOND_PART_MULTIPLE',
                'sunt momentan blocate!'
              )),
        { variant: 'warning', autoHideDuration: 6000 }
      );
    }
  };

  // Order-level "interrogate all": order-specific gating + the shared per-part interrogate in parallel.
  const interogateAll = async () => {
    getInactiveWorkflowSuppliers();
    try {
      const parts = orderpartsList.filter((op) => op.hasBeenVerifiedBySupplier);
      await Promise.all(parts.map((op) => interogate(op)));
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const validateOrder = (): boolean => {
    const nonInterogatedParts = orderpartsList.filter(
      (orderPart) => orderPart.hasBeenVerifiedBySupplier && !orderPart.supplierId
    );
    if (nonInterogatedParts.length > 0) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'ORDER_PLEASE_SELECT_PART',
          'Selectati piese pentru urmatoarele id-uri: '
        ) + nonInterogatedParts.map((x) => x.id).join(', '),
        { variant: 'warning' }
      );
      return true;
    }

    // Same validation as the alt-calc save (shared hook).
    const parts = orderpartsList.filter((x) => x.hasBeenVerifiedBySupplier);
    const { validated, errorString, decimalError, descriptionError, zeroPieceNrError } =
      validateSelectedParts(parts);
    const validatedById = new Map(validated.map((p) => [p.id, p]));
    setOrderparts((prev) => prev.map((p) => validatedById.get(p.id) ?? p));

    if (zeroPieceNrError) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'VALIDATION_QTY_ZERO_ERROR_MSG',
          'Cantitatea comandata trebuie sa fie mai mare decat 0!'
        ),
        { variant: 'warning' }
      );
      return true;
    }
    if (errorString) {
      enqueueSnackbar(errorString, { variant: 'warning', style: { whiteSpace: 'pre-line' } });
      return true;
    }
    if (decimalError) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'DECIMAL_ERROR_MSG',
          'Cantitatea comandata trebuie sa fie un numar intreg fara zecimale'
        ),
        { variant: 'warning' }
      );
      return true;
    }
    if (descriptionError) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'ALTERNATIVE_PART_DESCRIPTION_ERROR_MSG',
          'Va rugam completati descrierea piesei alternative: minim 3 caractere alfanumerice!'
        ),
        { variant: 'warning' }
      );
      return true;
    }
    return false;
  };

  const interrogateSupplierParts = (product: OrderPartDTO) =>
    caseService.InterogateCaseCalculationsAlternativePartsBySupplier({
      partId: product.id,
      partNumber: product.guideNumber,
      addition: additionTariff.value,
      discount: discountTariff.value,
      workflowId:
        caseSettingsState?.caseSettings?.workflow.id ?? order.case?.caseWorkflow?.workflowId,
      supplierId: order.supplierId ?? 0,
      organizationId: order.partsLocationOrganizationId,
      qty: product.pieceNr
    } as GetSuppliersAlternativePartsRequestDto);

  const checkIfOrderPartsChangedDuringOrderPlacement = async () => {
    const orderDatePlacement = new Date();
    let validityTimeOkay = true;
    const changedParts: CaseCalculationSupplierAlternativePart[] = [];

    orderpartsList.forEach((orderPart) => {
      orderPart.prevVal = null;
    });

    getInactiveWorkflowSuppliers();
    (await Promise.all(
      orderpartsList.map(async (product) => {
        if (!product.isDeleted) {
          const freshOrderedParts = await interrogateSupplierParts(product);
          const changedOrderedProducts = freshOrderedParts.filter(
            (part) =>
              part.supplierId == product.supplierId &&
              part.producer == product.producer &&
              part.alternativeGuideNumber == product.alternativeGuideNumber &&
              part.name == product.alternativePartName &&
              part.alternativePartCode == product.alternativePartCode &&
              part.orderCode == product.orderCode
          );
          if (changedOrderedProducts.length == 1) {
            const changedOrderedProduct = changedOrderedProducts[0];
            if (
              changedOrderedProduct.alternativePriceWithoutDiscount !== product.alternativePrice ||
              changedOrderedProduct.deliveryTime !== product.deliveryTime ||
              changedOrderedProduct.validityTime !== product.validityTime ||
              changedOrderedProduct.type != product.type ||
              changedOrderedProduct.orderCode != product.orderCode
            ) {
              changedParts.push({
                prevVal: _.cloneDeep(product),
                newVal: _.cloneDeep(changedOrderedProduct)
              });
              product.prevVal = { ...product };
              if (
                product.alternativePrice != changedOrderedProduct.alternativePriceWithoutDiscount
              ) {
                product.alternativePrice = changedOrderedProduct.alternativePriceWithoutDiscount;
              }
              if (product.deliveryTime != changedOrderedProduct.deliveryTime) {
                product.deliveryTime = changedOrderedProduct.deliveryTime;
                product.expectedDeliveryDate = changedOrderedProduct.expectedDeliveryDate || null;
              }
              if (product.type != changedOrderedProduct.type) {
                product.type = changedOrderedProduct.type;
              }
              if (product.orderCode != changedOrderedProduct.orderCode) {
                product.orderCode = changedOrderedProduct.orderCode;
              }
              if (product.validityTime != changedOrderedProduct.validityTime) {
                product.validityTime = changedOrderedProduct.validityTime;
              }
            }
            if (product.validityTime) {
              if (new Date(orderDatePlacement) > new Date(product.validityTime)) {
                validityTimeOkay = false;
                enqueueSnackbar(
                  'Codul: ' +
                    changedOrderedProduct.alternativeGuideNumber +
                    ' producator: ' +
                    changedOrderedProduct.producer +
                    ' furnizor: ' +
                    order.supplier?.name +
                    ' nu mai este valid. \n' +
                    'A fost valid pana la : ' +
                    moment
                      .utc(changedOrderedProduct.validityTime)
                      .local()
                      .format(appState!.longDateFormat),
                  { variant: 'warning' }
                );
              }
            }
          }
        }
      })
    )) || [];

    if (!validityTimeOkay) {
      return true;
    }

    if (changedParts.length > 0) {
      setIsChangeOrderStatusDialogOpen(false);
      setIsLoadingOrderDetails(false);

      enqueueSnackbar(
        translatorService.Tranlate('ERROR_MSG_SMTH', 'Produsele comandate au fost modificate'),
        { variant: 'warning' }
      );

      await Promise.all(
        changedParts.map(async (row) => {
          const part = row.newVal;
          const orderedPart = orderpartsList.filter(
            (product) =>
              part!.supplierId == product.supplierId &&
              part!.producer == product.producer &&
              part!.alternativeGuideNumber == product.alternativeGuideNumber &&
              part!.name == product.alternativePartName &&
              part.alternativePartCode == product.alternativePartCode
          )[0];
          await orderService.UpdateOrderPart(orderedPart!);
        })
      );

      const newOrder = await orderService.UpdateOrderAmounts({
        orderId: order.id
      } as UpdateOrderAmountsRequestDto);

      const next = { ...order };
      next.amountWithoutTVA = newOrder!.amountWithoutTVA;
      next.amountTVA = newOrder!.amountTVA;
      next.amount = newOrder!.amount;
      applyOrderChange(next);

      return true;
    }

    return false;
  };

  const placeSupplierOrder = async () => {
    const orderedPartsChanged = await checkIfOrderPartsChangedDuringOrderPlacement();
    if (orderedPartsChanged) {
      setExecuting(false);
      return;
    }
    const orderedPartStatus = orderPartStatus.find(
      (item) => item.code === OrderPartStatus.PROCESSING
    )!;
    const nextOrderStatus = statuses.find((item) => item.code === OrderStatusCode.REP_PLASATA);

    const request = {
      statusId: nextOrderStatus?.id,
      orderPartStatusId: orderedPartStatus.id,
      comment: changeOrderStatusComment,
      orderId: order.id,
      attachments: attachments,
      partsLocationOrganizationId: order.partsLocationOrganizationId,
      workflowId:
        caseSettingsState?.caseSettings?.workflow.id ?? order.case?.caseWorkflow?.workflowId,
      partsOrganizationId: appUser?.organizationId
    } as OrderStatusChangeRequestDto;

    await orderService.ChangeOrderStatus(request);

    orderpartsList.forEach((product) => {
      if (!product.isDeleted) {
        product.status = orderedPartStatus;
        product.statusId = orderedPartStatus.id;
      }
    });

    const orderStatusHistoryCopy = [...orderStatusHistory];

    const nextOrder = { ...order };
    nextOrder.orderStatus = nextOrderStatus!;
    nextOrder.orderStatusId = nextOrderStatus!.id;
    applyOrderChange(nextOrder);

    orderStatusHistoryCopy.unshift({
      id: 1,
      orderId: order.id,
      orderStatusId: order.orderStatusId,
      orderStatus: nextOrderStatus!,
      date: new Date(),
      createdBy: appUser!.id!,
      createdByUser: appUser,
      comment: changeOrderStatusComment,
      attachments: attachments.length > 0 ? attachments : null
    });

    if (
      order.supplier?.name == SupplierCode.MATEROM ||
      order.supplier?.name == SupplierCode.ADTOTAL ||
      order.supplier?.name == SupplierCode.CONEX ||
      order.supplier?.name == SupplierCode.EUROEST ||
      order.supplier?.name == SupplierCode.LKQ ||
      order.supplier?.name == SupplierCode.NESTE
    ) {
      const confirmedOrder = await orderService.GetOrder(order.id, false);
      if (confirmedOrder.supplierOrderNumber) {
        const proceedOrderStatus = orderStatuses.find(
          (item) => item.code === OrderStatusCode.FZR_COMANDA_CONFIRMATA
        );
        const proceedPartStatus = orderPartStatus.find(
          (item) => item.code === OrderPartStatus.PROCESSED
        )!;
        const proceedRequest = {
          statusId: proceedOrderStatus?.id,
          orderPartStatusId: proceedPartStatus.id,
          comment: '',
          orderId: order.id,
          attachments: [],
          partsLocationOrganizationId: order.partsLocationOrganizationId,
          workflowId:
            caseSettingsState?.caseSettings?.workflow.id ?? order.case?.caseWorkflow?.workflowId,
          partsOrganizationId: appUser?.organizationId
        } as OrderStatusChangeRequestDto;

        await orderService.ChangeOrderStatus(proceedRequest);

        orderpartsList.forEach((product) => {
          if (!product.isDeleted) {
            product.status = proceedPartStatus;
            product.statusId = proceedPartStatus.id;
          }
        });

        await caseService.SendConfirmedOrderNotification(order.caseId, order.id);

        const proceedOrder = { ...order };
        proceedOrder.orderStatus = proceedOrderStatus!;
        proceedOrder.orderStatusId = proceedOrderStatus!.id;
        applyOrderChange(proceedOrder);

        orderStatusHistoryCopy.unshift({
          id: 0,
          orderId: order.id,
          orderStatusId: order.orderStatusId,
          orderStatus: proceedOrderStatus!,
          date: new Date(),
          createdBy: appUser!.id!,
          createdByUser: appUser,
          comment: '',
          attachments: []
        });
      }
    }

    setOrderStatusHistory(orderStatusHistoryCopy);
    setIsChangeOrderStatusDialogOpen(false);
    setChangeOrderStatusComment('');
    setAttachments([]);
    setIsLoadingOrderDetails(false);
    setExecuting(false);
    setOrderparts((prev) => [...prev]);

    enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
    onOrderPlacedSuccessfully();
  };

  const updateSupplierOrder = async () => {
    try {
      const orderStatusHistoryCopy = [...orderStatusHistory];
      setIsLoadingOrderDetails(true);
      setExecuting(true);

      const orderedPartsChanged = [...orderpartsList]
        .filter((orderPart) => orderPart.hasBeenVerifiedBySupplier)
        .map((orderPart) => ({
          ...orderPart,
          alternativePartQuality: null,
          carPartCategory: null,
          supplier: null,
          status: null
        }));

      if (orderedPartsChanged.length > 0) {
        const updateOrderInfoRequest = {
          orderId: order.id,
          calculationId: firstCasePartToBeOrdered?.caseCalculationHistoryId ?? 0,
          parts: orderedPartsChanged
        } as UpdateOrderInfoRequest;

        await caseService.UpdateOrderInfo(updateOrderInfoRequest);

        const vin = caseSettingsState?.case
          ? caseSettingsState.case.caseVehicle.vin
          : order.caseVehicle.vin;
        const make = model ? model.brand : '';
        const modelDescription = model ? model.description : '';
        await Promise.all([
          orderedPartsChanged.map(async (orderPart) => {
            await caseService.AddNonStockPart({
              vin: vin,
              make: make,
              model: modelDescription,
              oeCode: orderPart.guideNumberBackup,
              oeSupplierCode:
                !R.isNil(quality) && quality.code === CarPartQualityTypeCode.OE
                  ? orderPart.guideNumber
                  : orderPart.alternativeGuideNumber ?? '',
              supplierId: order.supplierId
            } as NonStockPart);
          })
        ]);
      }

      const nextOrderStatus = getNextOrderStatus();
      const request = {
        statusId: nextOrderStatus?.id,
        comment: changeOrderStatusComment,
        orderId: order.id,
        attachments: attachments,
        partsLocationOrganizationId: order.partsLocationOrganizationId,
        workflowId:
          caseSettingsState?.caseSettings?.workflow.id ?? order.case?.caseWorkflow?.workflowId,
        partsOrganizationId: order.partsOrganizationId
      } as OrderStatusChangeRequestDto;
      await orderService.ChangeOrderStatus(request);

      if (orderedPartsChanged.length > 0) {
        const statusHistory = orderStatusHistory.find(
          (item) => item.orderStatus?.code == OrderStatusCode.FZR_VERIFICARE_LA_FURNIZOR
        );
        const notificationRequest = {
          orderId: order.id,
          orderComment: changeOrderStatusComment,
          caseId: order.caseId,
          template: OrderNotificationTemplateEnum.VERIFIED_BY_SUPPLIER,
          email: statusHistory ? statusHistory.createdByUser?.email ?? '' : ''
        };
        await caseService.SendVerifiedBySupplierOrderNotification(notificationRequest);
      }

      const nextOrder = { ...order };
      nextOrder.orderStatus = nextOrderStatus!;
      nextOrder.orderStatusId = nextOrderStatus!.id;
      applyOrderChange(nextOrder);

      orderStatusHistoryCopy.unshift({
        id: 1,
        orderId: order.id,
        orderStatusId: order.orderStatusId,
        orderStatus: nextOrderStatus!,
        date: new Date(),
        createdBy: appUser!.id!,
        createdByUser: appUser,
        comment: changeOrderStatusComment,
        attachments: attachments.length > 0 ? attachments : null
      });

      if (orderedPartsChanged.length > 0) {
        setOrderStatusHistory(orderStatusHistoryCopy);
        setIsLoadingOrderDetails(false);
        setIsChangeOrderStatusDialogOpen(false);
        setExecuting(false);
        enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
        return;
      }

      setOrderStatusHistory(orderStatusHistoryCopy);
      await placeSupplierOrder();
    } catch (ex) {
      setIsLoadingOrderDetails(false);
      setExecuting(false);
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const updateOrderAfterInterogation = async () => {
    try {
      const orderStatusHistoryCopy = [...orderStatusHistory];
      setIsLoadingOrderDetails(true);
      setExecuting(true);

      const orderParts = orderpartsList.concat(getPackageFromOrderParts(orderpartsList));
      const orderedPartsChanged = [...orderParts]
        .filter((orderPart) => orderPart.hasBeenVerifiedBySupplier)
        .map((orderPart) => ({
          ...orderPart,
          alternativePartQuality: null,
          carPartCategory: null,
          supplier: null,
          status: null
        }));

      if (orderedPartsChanged.length > 0) {
        const updateOrderInfoRequest = {
          orderId: order.id,
          calculationId: firstCasePartToBeOrdered?.caseCalculationHistoryId ?? 0,
          parts: orderedPartsChanged
        } as UpdateOrderInfoRequest;
        await caseService.UpdateOrderInfo(updateOrderInfoRequest);
      }

      const nextOrderStatus = getNextOrderStatus();
      const request = {
        statusId: nextOrderStatus?.id,
        comment: changeOrderStatusComment,
        orderId: order.id,
        attachments: attachments,
        partsLocationOrganizationId: order.partsLocationOrganizationId,
        workflowId:
          caseSettingsState?.caseSettings?.workflow.id ?? order.case?.caseWorkflow?.workflowId,
        partsOrganizationId: order.partsOrganizationId
      } as OrderStatusChangeRequestDto;
      await orderService.ChangeOrderStatus(request);

      const refreshedOrder = await orderService.GetOrder(order.id, true);
      applyOrderChange(mergeRefreshedOrder(refreshedOrder, refreshedOrder.orderParts ?? []));

      orderStatusHistoryCopy.unshift({
        id: 1,
        orderId: order.id,
        orderStatusId: order.orderStatusId,
        orderStatus: nextOrderStatus!,
        date: new Date(),
        createdBy: appUser!.id!,
        createdByUser: appUser,
        comment: changeOrderStatusComment,
        attachments: attachments.length > 0 ? attachments : null
      });

      setOrderStatusHistory(orderStatusHistoryCopy);
      setIsLoadingOrderDetails(false);
      setIsChangeOrderStatusDialogOpen(false);
      setExecuting(false);
      setOrderparts(refreshedOrder.orderParts!.map(initOrderPartDTO));

      enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
    } catch (ex) {
      setIsLoadingOrderDetails(false);
      setExecuting(false);
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const changeOrderStatusSubmit = async () => {
    const currentStatus = getStatusValue();
    if (currentStatus?.code === OrderStatusCode.FZR_VERIFICARE_LA_FURNIZOR) {
      await updateSupplierOrder();
      return;
    }
    if (currentStatus?.code === OrderStatusCode.REP_VERIFICATA_DE_FURNIZOR && someRowExpanded()) {
      const hasError = validateOrder();
      if (hasError) {
        return;
      }
      await updateOrderAfterInterogation();
      return;
    }

    if (order.interogatePartner && !order.partsLocationOrganizationId) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'ORDER_WITHOUT_DELIVERY_ADRESS_MSG',
          'Comanda nu poata fi lansata pentru ca nu are adresa de livrare!'
        ),
        { variant: 'warning' }
      );
      return;
    }

    try {
      setIsLoadingOrderDetails(true);
      setExecuting(true);
      const orderedPartsChanged = await checkIfOrderPartsChangedDuringOrderPlacement();
      if (orderedPartsChanged) {
        setExecuting(false);
        return;
      }
      const orderedPartStatus = orderPartStatus.find(
        (item) => item.code === OrderPartStatus.PROCESSING
      )!;
      const nextOrderStatus = getNextOrderStatus();

      const request = {
        statusId: nextOrderStatus?.id,
        orderPartStatusId: orderedPartStatus.id,
        comment: changeOrderStatusComment,
        orderId: order.id,
        attachments: attachments,
        partsLocationOrganizationId: order.partsLocationOrganizationId,
        workflowId:
          caseSettingsState?.caseSettings?.workflow.id ?? order.case?.caseWorkflow?.workflowId,
        partsOrganizationId: appUser?.organizationId
      } as OrderStatusChangeRequestDto;

      await orderService.ChangeOrderStatus(request);

      orderpartsList.forEach((product) => {
        if (!product.isDeleted) {
          product.status = orderedPartStatus;
          product.statusId = orderedPartStatus.id;
        }
      });

      const orderStatusHistoryCopy = [...orderStatusHistory];

      const nextOrder = { ...order };
      nextOrder.orderStatus = nextOrderStatus!;
      nextOrder.orderStatusId = nextOrderStatus!.id;
      applyOrderChange(nextOrder);

      orderStatusHistoryCopy.unshift({
        id: 1,
        orderId: order.id,
        orderStatusId: order.orderStatusId,
        orderStatus: nextOrderStatus!,
        date: new Date(),
        createdBy: appUser!.id!,
        createdByUser: appUser,
        comment: changeOrderStatusComment,
        attachments: attachments.length > 0 ? attachments : null
      });

      if (
        order.supplier?.name == SupplierCode.MATEROM ||
        order.supplier?.name == SupplierCode.ADTOTAL ||
        order.supplier?.name == SupplierCode.CONEX ||
        order.supplier?.name == SupplierCode.EUROEST ||
        order.supplier?.name == SupplierCode.LKQ ||
        order.supplier?.name == SupplierCode.NESTE
      ) {
        const confirmedOrder = await orderService.GetOrder(order.id, false);
        if (confirmedOrder.supplierOrderNumber) {
          const proceedOrderStatus = orderStatuses.find(
            (item) => item.code === OrderStatusCode.FZR_COMANDA_CONFIRMATA
          );
          const proceedPartStatus = orderPartStatus.find(
            (item) => item.code === OrderPartStatus.PROCESSED
          )!;
          const proceedRequest = {
            statusId: proceedOrderStatus?.id,
            orderPartStatusId: proceedPartStatus.id,
            comment: '',
            orderId: order.id,
            attachments: [],
            partsLocationOrganizationId: order.partsLocationOrganizationId,
            workflowId:
              caseSettingsState?.caseSettings?.workflow.id ?? order.case?.caseWorkflow?.workflowId,
            partsOrganizationId: appUser?.organizationId
          } as OrderStatusChangeRequestDto;

          await orderService.ChangeOrderStatus(proceedRequest);

          orderpartsList.forEach((product) => {
            if (!product.isDeleted) {
              product.status = proceedPartStatus;
              product.statusId = proceedPartStatus.id;
            }
          });

          await caseService.SendConfirmedOrderNotification(order.caseId, order.id);

          const proceedOrder = { ...order };
          proceedOrder.orderStatus = proceedOrderStatus!;
          proceedOrder.orderStatusId = proceedOrderStatus!.id;
          applyOrderChange(proceedOrder);

          orderStatusHistoryCopy.unshift({
            id: 0,
            orderId: order.id,
            orderStatusId: order.orderStatusId,
            orderStatus: proceedOrderStatus!,
            date: new Date(),
            createdBy: appUser!.id!,
            createdByUser: appUser,
            comment: '',
            attachments: []
          });
        }
      }

      await automatePlaceOrderChange();
      setOrderStatusHistory(orderStatusHistoryCopy);
      setIsChangeOrderStatusDialogOpen(false);
      setChangeOrderStatusComment('');
      setAttachments([]);
      setIsLoadingOrderDetails(false);
      setExecuting(false);
      setOrderparts((prev) => [...prev]);

      enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
      onOrderPlacedSuccessfully();
    } catch (ex) {
      setIsLoadingOrderDetails(false);
      setExecuting(false);
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const changeOrderStatusClick = async () => {
    const currentStatus = getStatusValue();
    if (currentStatus?.code === OrderStatusCode.REP_VERIFICATA_DE_FURNIZOR) {
      const hasError = validateOrder();
      if (hasError) {
        return;
      }
    }
    setIsChangeOrderStatusDialogOpen(true);
  };

  const handleExtendDelivery = async (partId: number) => {
    const part = selectedOrderPart;
    const days = extensionDays;
    if (!part || days === null || days <= 0) {
      enqueueSnackbar(
        translatorService.Tranlate('EXTEND_DELIVERY_INVALID_DAYS', 'Numar de zile invalid'),
        { variant: 'warning' }
      );
      return;
    }
    if (isNullOrUndefined(part.expectedDeliveryDate)) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'EXTEND_DELIVERY_NO_EXPECTED_DATE',
          'Piesa nu are data de livrare setata'
        ),
        { variant: 'warning' }
      );
      return;
    }

    setIsExtendingDelivery(true);
    try {
      const result: ExtendDeliveryResponseDto = await orderService.ExtendDelivery({
        orderPartId: part.id,
        currentExpectedDeliveryDate: part.expectedDeliveryDate,
        extensionDaysAdded: days
      } as ExtendDeliveryRequestDto);

      if (!result || !result.success) {
        const messageKey = result?.message || 'EXTEND_DELIVERY_ERROR';
        enqueueSnackbar(translatorService.Tranlate(messageKey, messageKey), { variant: 'warning' });
        return;
      }

      const newList = [...orderpartsList];
      const idx = newList.findIndex((item) => item.id === part.id);
      newList[idx] = {
        ...newList[idx],
        deliveryExtensionDays: (newList[idx].deliveryExtensionDays ?? 0) + days,
        expectedDeliveryDate: result.data?.deliveryDate ?? null
      };
      setOrderparts(newList);
      enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    } finally {
      setIsExtendingDelivery(false);
    }
  };

  const handleMarkAsDelivered = async (partId: number) => {
    try {
      const newArray = [...orderpartsList];
      const selectedPart = newArray.find((item) => item.id === partId);
      const groupedPartIds = newArray
        .filter((item) => item.groupedUniqueId === selectedPart?.groupedUniqueId)
        .map((item) => item.id);
      const idsToUpdate = groupedPartIds.length > 0 ? groupedPartIds : [partId];

      const updatedParts = await orderService.UpdateOrderPartStatus({
        orderPartIds: idsToUpdate,
        statusCode: OrderPartStatus.LIVRATA,
        shouldRequery: false
      });

      const refreshedOrder = await orderService.GetOrder(order.id, false);
      const orderStatusHistory = await orderService.GetOrderStatusHistories(order.id);

      updatedParts.forEach((updatedPart) => {
        const idx = newArray.findIndex((item) => item.id === updatedPart.id);
        if (idx !== -1) newArray[idx] = { ...newArray[idx], ...updatedPart } as OrderPartDTO;
      });

      applyOrderChange(mergeRefreshedOrder(refreshedOrder, newArray));
      setOrderparts(newArray);
      setOrderStatusHistory(orderStatusHistory);

      enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const handleCancelPartRequeryConfirm = async (shouldRequery: boolean) => {
    if (pendingUpdateStatusPartId == null) return;
    try {
      const newArray = [...orderpartsList];
      const selectedPart = newArray.find((item) => item.id === pendingUpdateStatusPartId);
      const groupedPartIds = newArray
        .filter((item) => item.groupedUniqueId === selectedPart?.groupedUniqueId)
        .map((item) => item.id);
      const idsToUpdate = groupedPartIds.length > 0 ? groupedPartIds : [pendingUpdateStatusPartId];

      const updatedParts = await orderService.UpdateOrderPartStatus({
        orderPartIds: idsToUpdate,
        statusCode: OrderPartStatus.ANULATA,
        shouldRequery
      });

      const refreshedOrder = await orderService.GetOrder(order.id, false);
      const orderStatusHistory = await orderService.GetOrderStatusHistories(order.id);

      updatedParts.forEach((updatedPart) => {
        const idx = newArray.findIndex((item) => item.id === updatedPart.id);
        if (idx !== -1) newArray[idx] = { ...newArray[idx], ...updatedPart } as OrderPartDTO;
      });

      applyOrderChange(mergeRefreshedOrder(refreshedOrder, newArray));
      setOrderparts(newArray);
      setOrderStatusHistory(orderStatusHistory);
      setIsCancelPartRequeryDialogOpen(false);
      setPendingUpdateStatusPartId(null);

      enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const deleteOrderPart = (id: number) => {
    setPendingDeletePartId(id);
    setIsRequeryDialogOpen(true);
  };

  const handleRequeryConfirm = async (shouldRequery: boolean) => {
    if (pendingDeletePartId == null) return;
    try {
      const newArray = [...orderpartsList];
      const selectedPartIndex = newArray.findIndex((item) => item.id === pendingDeletePartId);
      const part = newArray[selectedPartIndex];
      const groupedPartIds = newArray
        .filter((item) => item.groupedUniqueId === part.groupedUniqueId)
        .map((item) => item.id);

      await orderService.DeleteOrderPartWithChoice({ orderPartIds: groupedPartIds, shouldRequery });

      newArray.forEach((item) => {
        if (groupedPartIds.includes(item.id)) item.isDeleted = true;
      });
      const refreshedOrder = await orderService.GetOrder(order.id, false);
      const orderStatusHistory = await orderService.GetOrderStatusHistories(order.id);

      applyOrderChange(mergeRefreshedOrder(refreshedOrder, newArray));
      setOrderparts(newArray);
      setOrderStatusHistory(orderStatusHistory);
      setIsRequeryDialogOpen(false);
      setPendingDeletePartId(null);

      enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const handleOpenHistory = async (partId: number) => {
    try {
      const history = await orderService.GetOrderPartDeliveryDateHistory(partId);
      setHistoryDialogData(history);
      setIsHistoryDialogOpen(true);
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const downloadFile = async (fileName: string, origFileName: string) => {
    const fileBlob = await caseService.GetCaseAttachment(fileName);
    FileSaver.saveAs(fileBlob, origFileName);
  };

  const handleUploadAttachment = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const next = [...attachments];
      next.push({
        id: 0,
        caseId: order.caseId,
        attachmentTypeId: attachType!.id,
        attachmentType: attachType,
        file: e.target.files![0],
        fileName: e.target.files![0].name,
        uploadDate: new Date(),
        carPhotos: false,
        originalFileName: e.target.files![0].name,
        caseRequestId: null,
        paymentId: null,
        serviceAppointmentId: null,
        caseClientId: null,
        isSelection: false
      } as unknown as CaseAttachment);
      setAttachments(next);
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const handleDeleteAttachment = (id: number, fileName: string) => {
    const next = [...attachments];
    const index = next.findIndex((item) => item.id === id);
    next.splice(index, 1);
    setAttachments(next);
  };

  const t = (key: string, def: string) => translatorService.Tranlate(key, def);
  const currencySuffix =
    !isNullOrUndefined(order.currencyCode) && order.currencyCode !== '' ? order.currencyCode : '';
  const vehicle = caseSettingsState?.case?.caseVehicle ?? order.caseVehicle;
  const currentStatus = getStatusValue();
  const showInvoiceColumns = isDisplayInvoiceColumn();
  const showActionsColumn = isDeliveryActionsVisible();
  // Idem old: rows expand / interrogate only in REP_VERIFICATA_DE_FURNIZOR for non-supplier users.
  const expandVerified =
    currentStatus?.code === OrderStatusCode.REP_VERIFICATA_DE_FURNIZOR && !hasSupplierRole;
  const expandableEnabled =
    expandVerified && orderpartsList.some((op) => op.hasBeenVerifiedBySupplier);
  const orderPartsHelper = new OrderParts(orderpartsList, 0);

  const withInfoIcon = (content: React.ReactNode) => (
    <span>
      {content}
      <span style={{ display: 'inline-block', verticalAlign: 'text-bottom', marginLeft: 4 }}>
        <CustomIcon name="info" />
      </span>
    </span>
  );

  const displayOtherOrderDetails = (part: OrderPartDTO) => {
    const stock =
      part.alternativePartQualityId === null
        ? part.availableStock
        : part.availableQuantity === null
        ? ''
        : part.availableQuantity;
    const location =
      part.alternativePartQualityId === null
        ? isNull(part.location, '')
        : isNull(part.alternativePartStockLocation, '');
    const infoRows: [string, React.ReactNode][] = [
      [t('ORDER_PART_DETAILS_STOCK_LABEL', 'Disponibil'), stock],
      [t('ORDER_PART_DETAILS_LOCATION_LABEL', 'Locatie'), location]
    ];
    const info = (
      <div>
        {infoRows.map((row) => (
          <div key={row[0]}>
            {row[0]}: {row[1] != null && row[1] !== '' ? row[1] : '-'}
          </div>
        ))}
      </div>
    );
    return (
      <Tooltip title={info}>
        {withInfoIcon(
          isNullOrUndefined(part.producer) || part.producer === '' ? 'N/A' : part.producer
        )}
      </Tooltip>
    );
  };

  const displayDeliveryOrderDetails = (part: OrderPartDTO) => (
    <Box component="div" style={{ paddingRight: 5, wordBreak: 'break-word' }}>
      {isNullOrUndefined(part.deliveryTime) ? '' : part.deliveryTime}
      {!isNullOrUndefined(part.prevVal) && (
        <Box component="div">
          {t('ORDER_PART_DETAILS_DELIVERY_TIME_LABEL11', 'Livrare valoare anterioara') +
            ': ' +
            (isNullOrUndefined(part.prevVal?.deliveryTime) ? '' : part.prevVal?.deliveryTime)}
        </Box>
      )}
    </Box>
  );

  const renderCodeOe = (part: OrderPartDTO) => {
    if (currentStatus?.code === OrderStatusCode.FZR_VERIFICARE_LA_FURNIZOR) {
      if (!R.isNil(quality) && quality.code === CarPartQualityTypeCode.OE && hasSupplierRole) {
        return (
          <CustomInputText
            size="small"
            fullWidth
            style={{ marginBottom: 0 }}
            value={part.guideNumber ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleOECodeChange(e as any, part)
            }
            disabled={part.excludeFromOrder ?? false}
          />
        );
      }
    }
    if (
      !R.isNil(quality) &&
      quality.code === CarPartQualityTypeCode.OE &&
      part.hasEmptyGuideNumber
    ) {
      return (
        <CustomInputText
          size="small"
          fullWidth
          style={{ marginBottom: 0 }}
          value={part.guideNumber ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleClientOECodeChange(e as any, part)
          }
          disabled={part.excludeFromOrder ?? false}
        />
      );
    }
    return part.guideNumber;
  };

  const renderCodeAm = (part: OrderPartDTO) => {
    if (currentStatus?.code === OrderStatusCode.FZR_VERIFICARE_LA_FURNIZOR) {
      if (!R.isNil(quality) && quality.code === CarPartQualityTypeCode.AM && hasSupplierRole) {
        return (
          <CustomInputText
            size="small"
            fullWidth
            style={{ marginBottom: 0 }}
            value={part.alternativeGuideNumber ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleAMCodeChange(e as any, part)
            }
            disabled={part.excludeFromOrder ?? false}
          />
        );
      }
    }
    if (
      !R.isNil(quality) &&
      quality.code === CarPartQualityTypeCode.AM &&
      part.hasEmptyAlternativeGuideNumber
    ) {
      return (
        <CustomInputText
          size="small"
          fullWidth
          style={{ marginBottom: 0 }}
          value={part.alternativeGuideNumber ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleClientAMCodeChange(e as any, part)
          }
          disabled={part.excludeFromOrder ?? false}
        />
      );
    }
    return part.alternativeGuideNumber;
  };

  const renderNameCell = (part: OrderPartDTO) => {
    const name =
      hasSupplierRole && part.alternativePartDescription
        ? part.alternativePartDescription
        : !isNullOrUndefined(part.alternativePartQualityId)
        ? part.alternativePartName
        : part.name;
    if (!part.alternativePartDescription) {
      return name;
    }
    return (
      <span>
        <Tooltip
          title={
            <div>
              <div>
                {translatorService.Tranlate(
                  'CALCULATION_DETAILS_ALTERNATIVE_PART_DESCRIPTION_LABEL',
                  'Descriere piesa alternativa'
                ) +
                  ': ' +
                  part.alternativePartDescription}
              </div>
            </div>
          }
        >
          {withInfoIcon(name)}
        </Tooltip>
        {renderAlternativePartDescriptionCopy(part.alternativePartDescription)}
      </span>
    );
  };

  const renderQualityCell = (part: OrderPartDTO) => {
    if (part.alternativePartQualityId === null) {
      return part.carPartCategory!.displayName;
    }
    if (isNullOrUndefined(part.alternativePartQuality)) {
      return '';
    }
    return part.alternativePartQuality.displayName;
  };

  const renderPieceNrCell = (
    part: OrderPartDTO,
    onQuantityChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  ) => {
    if (!isNullOrUndefined(part.prevVal) && part.prevVal.pieceNr !== part.pieceNr) {
      return (
        <input
          disabled
          value={part.pieceNr || ''}
          title={'Valoare Anterioara: ' + part.prevVal.pieceNr}
        />
      );
    }
    if (
      currentStatus?.code === OrderStatusCode.REP_VERIFICATA_DE_FURNIZOR &&
      !hasSupplierRole &&
      part.hasBeenVerifiedBySupplier
    ) {
      // Quantity edit routes through the shared hook (>10 warning + stock excess handled there).
      return (
        <CustomInputText
          size="small"
          fullWidth
          style={{ marginBottom: 0 }}
          error={part.unitsError}
          disabled={part.excludeFromOrder ?? false}
          InputProps={{ inputComponent: NumberFormatCustom }}
          inputProps={{ style: { textAlign: 'right' } }}
          value={part.pieceNr == null ? '' : part.pieceNr}
          onChange={onQuantityChange}
        />
      );
    }
    if (
      part.status?.code === OrderPartStatus.CONFIRMATA_PARTIAL &&
      !isNullOrUndefined(part.orderedPieceNr)
    ) {
      return (
        <Tooltip
          title={`${t('ORDER_PART_CONFIRMED_PIECES_LABEL', 'Confirmate de furnizor')}: ${
            part.pieceNr
          } / ${part.orderedPieceNr} ${t('ORDER_PART_ORDERED_PIECES_LABEL', 'comandate')}`}
        >
          <span>
            {part.pieceNr} / {part.orderedPieceNr}
          </span>
        </Tooltip>
      );
    }
    return part.pieceNr;
  };

  const orderPartOf = (part: CaseCalculationPart): OrderPartDTO =>
    orderpartsList.find((o) => o.id === part.id) ?? (part as unknown as OrderPartDTO);

  const renderRowActionsCell = (part: CaseCalculationPart) => {
    const op = orderPartOf(part);
    return (
      <Box className={cx(classes.row, classes.justifyEnd, classes.gap8)}>
        {op.isExpanded && (
          <Tooltip title={t('INTEROGATE', 'Interogheaza')} placement="top">
            <ButtonBase
              className={cx(classes.iconBtn, classes.iconBtnSm, classes.iconBtnPrimary)}
              onClick={() => interogateAlternativeParts(part)}
            >
              <CustomIcon name="refresh" />
            </ButtonBase>
          </Tooltip>
        )}
        {op.isDeleted ? (
          <span className={classes.dangerText}>{t('ORDER_PART_DETAILS_DELETED_LABEL', 'Sters')}</span>
        ) : order.clientId !== appUser!.organizationId ? null : viewDeletePart() && hasRights ? (
          <Tooltip title={t('ORDER_PART_DELETE_LABEL', 'Sterge piesa')} placement="top">
            <ButtonBase
              className={cx(classes.iconBtn, classes.iconBtnSm, classes.iconBtnDanger)}
              onClick={() => deleteOrderPart(op.id)}
            >
              <CustomIcon name="trash" />
            </ButtonBase>
          </Tooltip>
        ) : null}
      </Box>
    );
  };

  const renderDeliveryActionsCell = (part: CaseCalculationPart) => {
    const op = orderPartOf(part);
    return (
      <Box className={cx(classes.row, classes.justifyEnd, classes.gap8)}>
        <Tooltip title={t('ORDER_PART_EXTEND_DELIVERY_LABEL', 'Prelungire livrare')} placement="top">
          <span>
            <ButtonBase
              className={cx(classes.iconBtn, classes.iconBtnSm)}
              disabled={!canUpdateOrderPartStatus(op) || isNullOrUndefined(op.expectedDeliveryDate)}
              onClick={() => {
                setSelectedOrderPart(op);
                setExtensionDays(null);
                setIsExtendDialogOpen(true);
              }}
            >
              <CustomIcon name="edit" />
            </ButtonBase>
          </span>
        </Tooltip>
        <Tooltip title={t('ORDER_PART_MARK_DELIVERED_LABEL', 'Marcat livrat')} placement="top">
          <span>
            <ButtonBase
              className={cx(classes.iconBtn, classes.iconBtnSm)}
              style={{ color: canUpdateOrderPartStatus(op) ? 'green' : undefined }}
              disabled={!canUpdateOrderPartStatus(op)}
              onClick={() => handleMarkAsDelivered(op.id)}
            >
              <CustomIcon name="checkCircle" />
            </ButtonBase>
          </span>
        </Tooltip>
        <Tooltip title={t('ORDER_PART_CANCEL_LABEL', 'Anuleaza piesa')} placement="top">
          <span>
            <ButtonBase
              className={cx(classes.iconBtn, classes.iconBtnSm)}
              style={{ color: canUpdateOrderPartStatus(op) ? 'red' : undefined }}
              disabled={!canUpdateOrderPartStatus(op)}
              onClick={() => {
                setPendingUpdateStatusPartId(op.id);
                setIsCancelPartRequeryDialogOpen(true);
              }}
            >
              <CustomIcon name="xCircle" />
            </ButtonBase>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const orderGridCtx: OrderGridColumnsCtx = {
    t,
    showInvoiceColumns,
    showActionsColumn,
    renderId: (part) => orderPartOf(part).id,
    renderCodeOe: (part) => renderCodeOe(orderPartOf(part)),
    renderCodeAm: (part) => renderCodeAm(orderPartOf(part)),
    renderName: (part) => renderNameCell(orderPartOf(part)),
    renderQuality: (part) => renderQualityCell(orderPartOf(part)),
    renderUnitPrice: (part) => {
      const op = orderPartOf(part);
      const current = pretUnitarFormat(op, op.originalPrice, order.currencyCode, country);
      // Price changed during placement: show previous value, even when prices are hidden (idem old).
      if (!isNullOrUndefined(op.prevVal)) {
        const previous = pretUnitarFormat(
          op.prevVal,
          op.prevVal!.originalPrice,
          order.currencyCode,
          country
        );
        if (previous !== current) {
          return (
            <CustomInputText
              size="small"
              fullWidth
              disabled
              style={{ marginBottom: 0 }}
              label={'Valoare Anterioara: ' + previous}
              value={current || ''}
            />
          );
        }
      }
      return showOrderPrice ? current : '';
    },
    renderPieceNr: (part, onQuantityChange) =>
      renderPieceNrCell(orderPartOf(part), onQuantityChange),
    renderDiscount: (part) => {
      const op = orderPartOf(part);
      return op.alternativePartQualityId === null ? op.discount : op.alternativePartDiscount;
    },
    renderTotalPrice: (part) => {
      const op = orderPartOf(part);
      return showOrderPrice
        ? Utils.countryNumberFormat(orderPartsHelper.getTotalPrice(op).toFixed(2), country) +
            ' ' +
            currencySuffix
        : '';
    },
    renderProducer: (part) => displayOtherOrderDetails(orderPartOf(part)),
    renderDelivery: (part) => displayDeliveryOrderDetails(orderPartOf(part)),
    renderExpectedDelivery: (part) => {
      const op = orderPartOf(part);
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>
            {op.expectedDeliveryDate
              ? formatDeliveryDate(op.expectedDeliveryDate, appState!.longDateFormat)
              : ''}
          </span>
          {op.expectedDeliveryDate && (
            <Tooltip
              title={t('ORDER_PART_DELIVERY_HISTORY_LABEL', 'Istoric livrare')}
              placement="top"
            >
              <ButtonBase onClick={() => handleOpenHistory(op.id)} style={{ padding: 2 }}>
                <CustomIcon name="clock" />
              </ButtonBase>
            </Tooltip>
          )}
        </div>
      );
    },
    renderDeliveryNotice: (part) => orderPartOf(part).processedDeliveryNotices ?? '',
    renderInvoiceNumber: (part) => orderPartOf(part).processedInvoiceNumbers ?? '',
    renderStatus: (part) => orderPartOf(part).status!.displayName,
    renderRowActions: renderRowActionsCell,
    renderActions: renderDeliveryActionsCell
  };

  // Sort/filter disabled for the order grid.
  const orderColumns = buildOrderGridColumns(orderGridCtx).map((col) => ({
    ...col,
    sortKey: null,
    filterable: false
  }));

  const noLoop = () => undefined;

  const orderPartActions: CalculationActions = {
    registerRow: noLoop,
    toggleExpand,
    interogate,
    interogateAlternativeParts,
    interogateAll: () => interogateAll(),
    resetAlternatives: noLoop,
    toggleSelect: toggleAlternativePartSelection,
    setRecommended: (partId, ap) => {
      handleAlternativePartSelect(ap.id, partId, ap);
      // idem old: mark the part dirty so the alternative selection is persisted on save.
      setOrderparts((prev) => prev.map((p) => (p.id === partId ? { ...p, toBeUpdated: true } : p)));
    },
    clearRecommendation,
    unselectPart,
    unselectAll,
    deletePart: noLoop,
    sendNotificationToSupport: () => Promise.resolve(),
    addAlternativePart: noLoop,
    importOriginalParts: noLoop,
    importAlternativeParts: noLoop,
    updateGuideNumber,
    updatePieceNr,
    updateOriginalPrice,
    updateCarPartCategory,
    updateAlternativePartDescription,
    canOptimize: () => false,
    openOptimize: noLoop,
    optimizeByBestPrice: noLoop,
    optimizeByBestQuality: noLoop,
    applyOptimal: noLoop,
    isOptimizingLoading: false,
    isApplyingOptimalLoading: false,
    addCalculationPart: noLoop,
    copyCalculationPart: noLoop
  };

  // Interogation-result columns (differ from the order columns); built once for a stable identity.
  const [altColumns] = useState(() =>
    buildAltCalcGridColumns({
      workflowCalculation: ORDER_PART_WORKFLOW_CALCULATION,
      qualities: [],
      actions: orderPartActions,
      onCopyPart: noLoop,
      c: classes,
      t,
      country,
      variantsEnabled: false,
      showNetPrice: ORDER_PART_WORKFLOW_CALCULATION.alternativeNetPrice ?? false,
      caseIsOffline: false
    })
  );

  const onVariantSort = (id: number) => (key: ColumnKey) =>
    setVariantSort((prev) => {
      const cur = prev[id] ?? { column: null, dir: 1 };
      const next: SortState =
        cur.column !== key
          ? { column: key, dir: 1 }
          : cur.dir === 1
          ? { column: key, dir: -1 }
          : { column: null, dir: 1 };
      return { ...prev, [id]: next };
    });
  const onVariantApplyFilter = (id: number) => (key: ColumnKey, value: ColumnFilter | null) =>
    setVariantFilters((prev) => {
      const cur = { ...(prev[id] ?? {}) };
      if (value == null) delete cur[key];
      else cur[key] = value;
      return { ...prev, [id]: cur };
    });

  const isSaveDisabled = canChangeNextOrderStatus() || !allPartsInterogated();

  const hasOrderPartsChangedBySupplier =
    hasSupplierRole &&
    order?.orderStatus?.code === OrderStatusCode.FZR_VERIFICARE_LA_FURNIZOR &&
    !checkIfOrderPartsVerifiedBySupplier();

  const tvaValue = parseFloat(TVA) || 0;

  const renderAlternativePartDescriptionCopy = (description: string) => (
    <CopyButton
      value={description}
      tooltip={translatorService.Tranlate(
        'COPY_ALTERNATIVE_PART_DESCRIPTION',
        'Copiaza descriere piesa alternativa'
      )}
      onCopied={() =>
        enqueueSnackbar(
          translatorService.Tranlate(
            'ALTERNATIVE_PART_DESCRIPTION_COPIED',
            'Descriere piesa alternativa copiata cu succes'
          ),
          { variant: 'success' }
        )
      }
    />
  );

  return (
    <>
      <Box
        className={classes.pageBg}
        style={{
          minHeight: '100%',
          fontFamily: 'Montserrat',
          // Paint-only 32px grey ring into the layout gutter; no layout offset.
          ...(props.fullBleed ? { boxShadow: '0 0 0 32px #EDEFF5' } : {})
        }}
      >
        {isLoadingOrderDetails ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ minHeight: '60vh' }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, width: '100%' }}>
                <ScaleLoader color={'var(--primary)'} loading={true} />
              </div>
          </Box>
        ) : (
          <Box p={2}>
            <OrderDetailsHeader
              order={order}
              vehicle={vehicle}
              costCenter={costCenter}
              interventionType={interventionType}
              partsLocationOrganization={partsLocationOrganization}
              country={country}
              longDateFormat={appState!.longDateFormat}
              saveButtonLabel={getOrderButton()}
              hideSave={hideSaveButton()}
              saveDisabled={isSaveDisabled}
              saveLoading={executing}
              onSave={() =>
                automatePlaceOrder || workflowAutomatePlaceOrder
                  ? changeOrderStatusSubmit()
                  : changeOrderStatusClick()
              }
            />

            <Box mb={2} style={{ overflowX: 'auto' }}>
              <Box
                style={
                  {
                    minWidth: 'min-content',
                    ['--alt-grid-cols']: buildGridTemplateFrom(orderColumns, ['32px', '32px'])
                  } as React.CSSProperties
                }
              >
                <CalculationFlagsProvider variantsEnabled={false} isOffline={false}>
                  {expandableEnabled && (
                    <Box mb={2}>
                      <AlternativeCalculationToolbar
                        parts={orderpartsList}
                        activeMode={null}
                        deliveryAddresses={[]}
                        workflowCalculation={ORDER_PART_WORKFLOW_CALCULATION}
                        workflowOptimization={[]}
                        currentWorkflowContainsOrderFormOrInterogatePartner={false}
                        actions={orderPartActions}
                        onNotify={onNotify}
                        onActiveMode={noLoop}
                        onDeliveryChange={noLoop}
                        onClearFilters={noLoop}
                      />
                    </Box>
                  )}
                  <AlternativeCalculationPiecesHeader
                    columns={orderColumns}
                    sort={orderSort}
                    filters={orderFilters}
                    onSort={noLoop}
                    onApplyFilter={noLoop}
                    filterOptionsFor={(key) => filterOptionsFor(orderpartsList, key, orderFilters)}
                    filterOptionLabel={filterOptionLabel}
                    onUnselectAll={expandableEnabled ? unselectAll : null}
                    hasUnselectAllColumn={true}
                    hasRecoColumn={true}
                    flush={false}
                  />
                  {orderpartsList.map((op) => (
                    <AlternativeCalculationPart
                      key={op.id}
                      part={op}
                      columns={orderColumns}
                      canExpand={expandableEnabled && op.hasBeenVerifiedBySupplier}
                      flush={true}
                      sort={variantSortOf(op.id)}
                      filters={variantFiltersOf(op.id)}
                      suppliers={[]}
                      qualities={[]}
                      workflowCalculation={ORDER_PART_WORKFLOW_CALCULATION}
                      actions={orderPartActions}
                      onNotify={onNotify}
                      onCopyPart={noLoop}
                      enableLineFilterReset={false}
                      alternativePartsHeight={320}
                      infoTooltip={null}
                      variantsHeader={
                        <AlternativeCalculationPiecesHeader
                          columns={altColumns}
                          sort={variantSortOf(op.id)}
                          filters={variantFiltersOf(op.id)}
                          onSort={onVariantSort(op.id)}
                          onApplyFilter={onVariantApplyFilter(op.id)}
                          filterOptionsFor={(key) => filterOptionsFor([op], key, variantFiltersOf(op.id))}
                          filterOptionLabel={filterOptionLabel}
                          onUnselectAll={null}
                          hasUnselectAllColumn={true}
                          hasRecoColumn={true}
                          flush
                        />
                      }
                    />
                  ))}
                </CalculationFlagsProvider>
              </Box>
            </Box>

            {showOrderPrice && (
              <OrderDetailsTotals order={order} tva={tvaValue} currencyCode={order.currencyCode} />
            )}

            <Box style={{ backgroundColor: '#FFFFFF', borderRadius: 8, overflow: 'hidden' }}>
              <Box className={classes.sectionHeader}>
                {t('ORDER_DETAILS_HISTORY_TITLE', 'ISTORIC COMANDA')}
              </Box>
              <Box p={2}>
                <OrderHistoryTimeline
                  items={orderStatusHistory}
                  longDateFormat={appState!.longDateFormat}
                  emptyLabel={t('ORDER_DETAILS_HISTORY_NO_DATA', 'Nu exista date de afisat!')}
                  onDownload={downloadFile}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <ChangeOrderStatusDialog
        open={isChangeOrderStatusDialogOpen}
        comment={changeOrderStatusComment}
        required={!workflowAutomatePlaceOrder}
        executing={executing}
        hasOrderPartsChangedBySupplier={hasOrderPartsChangedBySupplier}
        attachments={attachments}
        onCommentChange={setChangeOrderStatusComment}
        onUploadAttachment={handleUploadAttachment}
        onDeleteAttachment={handleDeleteAttachment}
        onDownloadAttachment={downloadFile}
        onClose={() => setIsChangeOrderStatusDialogOpen(false)}
        onSubmit={() => changeOrderStatusSubmit()}
      />

      <RequeryDialog
        open={isRequeryDialogOpen}
        onConfirm={handleRequeryConfirm}
        onClose={() => {
          setIsRequeryDialogOpen(false);
          setPendingDeletePartId(null);
        }}
        title={t('ORDER_DETAILS_REQUERY_DIALOG_TITLE', 'Confirmare ștergere piesă')}
        message={t(
          'ORDER_DETAILS_REQUERY_DIALOG_MESSAGE',
          'Doriți să reinterogați piesa la un alt furnizor?'
        )}
        yesLabel={t('VEHICLE_SEARCH_FORM_VEHICLE_YES', 'Da')}
        noLabel={t('VEHICLE_SEARCH_FORM_VEHICLE_NO', 'Nu')}
      />

      <RequeryDialog
        open={isCancelPartRequeryDialogOpen}
        onConfirm={handleCancelPartRequeryConfirm}
        onClose={() => {
          setIsCancelPartRequeryDialogOpen(false);
          setPendingUpdateStatusPartId(null);
        }}
        title={t('ORDER_PART_CANCEL_DIALOG_TITLE', 'Confirmare anulare piesă')}
        message={t(
          'ORDER_PART_CANCEL_REQUERY_MESSAGE',
          'Doriți să reinterogați piesa la un alt furnizor?'
        )}
        yesLabel={t('VEHICLE_SEARCH_FORM_VEHICLE_YES', 'Da')}
        noLabel={t('VEHICLE_SEARCH_FORM_VEHICLE_NO', 'Nu')}
      />

      <ExtendDeliveryDialog
        open={isExtendDialogOpen}
        extensionDays={extensionDays}
        maxDays={MAX_EXTENSION_DAYS}
        onChange={setExtensionDays}
        onClose={() => setIsExtendDialogOpen(false)}
        isLoading={isExtendingDelivery}
        onSubmit={async () => {
          if (selectedOrderPart != null) {
            await handleExtendDelivery(selectedOrderPart.id);
            setIsExtendDialogOpen(false);
          }
        }}
      />

      <DeliveryDateHistoryDialog
        open={isHistoryDialogOpen}
        items={historyDialogData}
        onClose={() => setIsHistoryDialogOpen(false)}
      />
    </>
  );
};

export default OrderDetailsNew;
